import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchProducts, fetchProduct } from "@/lib/products";
import { categoryOf } from "@/lib/store";
import { trackView } from "@/lib/analytics";
import { useDelivery } from "@/lib/delivery";
import { SmoothScroll } from "@/components/site/SmoothScroll";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";
import { PhotoGallery } from "@/components/site/PhotoGallery";
import { Lightbox } from "@/components/site/Lightbox";
import { MatchingAccessories } from "@/components/site/MatchingAccessories";
import { StickyBuyBar } from "@/components/site/StickyBuyBar";
import { CompactProductCard } from "@/components/site/CompactProductCard";
import { useIsPhone } from "@/hooks/use-phone";
import { ProductDetails } from "@/components/site/ProductDetails";
import { ProductCard } from "@/components/site/ProductCard";
import { Reveal, Eyebrow } from "@/components/site/Reveal";

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [state, setState] = useState("loading");
  const [all, setAll] = useState([]);
  const [zoomAt, setZoomAt] = useState(null);
  const phone = useIsPhone();
  useDelivery(); // redesenha os links do WhatsApp quando o cliente consulta a entrega

  useEffect(() => {
    fetchProducts()
      .then(setAll)
      .catch(() => setAll([]));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setState((s) => (s === "ok" ? s : "loading"));
    fetchProduct(id)
      .then((p) => { setProduct(p ?? null); setState(p ? "ok" : "missing"); })
      .catch(() => setState("missing"));
  }, [id]);

  const related = useMemo(
    () => (product ? all.filter((p) => p.id !== product.id && categoryOf(p) === categoryOf(product)).slice(0, 3) : []),
    [all, product],
  );

  useEffect(() => {
    if (!product) return undefined;
    trackView(product);
    document.title = `${product.model} | RC iPhones`;
    return () => { document.title = "RC iPhones | iPhones novos e seminovos"; };
  }, [product]);

  return (
    <SmoothScroll>
      <div className="grain min-h-screen bg-[#09090b] text-white">
        <Header />
        <main className="mx-auto max-w-[1440px] px-5 sm:px-8 pt-[136px] lg:pt-32 pb-24">
          {state === "loading" && <div className="h-auto aspect-[4/5] max-w-xl animate-pulse rounded-[28px] bg-white/5 lg:max-w-[520px]" />}
          {state === "missing" && (
            <div data-testid="product-not-found" className="py-24 text-center">
              <p className="font-display text-4xl font-bold">Produto não encontrado.</p>
              <Link to="/#estoque" className="mt-8 inline-flex rounded-full bg-white px-6 py-3 text-sm font-semibold text-black">Ver produtos disponíveis</Link>
            </div>
          )}
          {state === "ok" && product && (
            <>
              <div className="grid grid-cols-1 gap-6 sm:gap-10 lg:grid-cols-12 lg:gap-14">
                <Reveal className="lg:col-span-7">
                  <div data-testid="product-page-photo" className="group relative mx-auto aspect-[4/5] w-full max-w-xl overflow-hidden rounded-[28px] border border-white/10 bg-zinc-950 lg:max-w-[520px]">
                    <div className="absolute inset-0 spotlight" aria-hidden="true" />
                    <div className={`relative h-full ${product.status === "vendido" ? "grayscale opacity-70" : ""}`}>
                      <PhotoGallery images={product.images} alt={product.model} id={`page-${product.id}`} onOpen={setZoomAt} />
                    </div>
                  </div>
                </Reveal>
                <Reveal delay={0.1} className="lg:col-span-5 lg:pt-6">
                  <ProductDetails p={product} all={all} />
                </Reveal>
              </div>
              {categoryOf(product) === "iphone" && product.status !== "vendido" && <MatchingAccessories product={product} all={all} />}
              {related.length > 0 && (
                <section data-testid="related-section" className="mt-24 sm:mt-32">
                  <Eyebrow>Você também pode gostar</Eyebrow>
                  <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
                    {related.map((p, i) => (phone ? <CompactProductCard key={p.id} p={p} index={i} /> : <ProductCard key={p.id} p={p} index={i} />))}
                  </div>
                </section>
              )}
            </>
          )}
        </main>
        <Footer />
        <FloatingWhatsApp className="hidden lg:flex" />
        {state === "ok" && product && product.status !== "vendido" && <><StickyBuyBar p={product} /><div className="h-24 lg:hidden" aria-hidden="true" /></>}
        {zoomAt !== null && product?.images?.length > 0 && (
          <Lightbox images={product.images.filter(Boolean)} start={zoomAt} alt={product.model} onClose={() => setZoomAt(null)} />
        )}
      </div>
    </SmoothScroll>
  );
}
